import type Map from 'ol/Map'
import type { Coordinate } from 'ol/coordinate'
import { parseErrorResponse } from '../api/client'
import { layerInfoOf, type WmsTileLayer } from './layers'
import { visibleProperties } from './layerAttributes'
import type { VectorModeController } from './vectorMode'

export interface FeatureHit {
  /** Qualified WMS name — resolved to the panel's friendly label at render time. */
  layerName: string
  layerTitle: string
  properties: Record<string, unknown>
}

/**
 * Click slack for shapes-mode hit testing, in screen pixels — converted to map
 * units with the view resolution. Matches the forgiveness a WMS GetFeatureInfo
 * gives for free.
 */
const HIT_TOLERANCE_PX = 5

interface GeoJsonFeatureCollection {
  features?: Array<{ properties?: Record<string, unknown> }>
}

/**
 * Feature attributes on click (PRD P0.6, D7 — core feature), layer by layer,
 * topmost first.
 *
 * Two paths, one order. A layer drawn as a map picture is asked to the server
 * through the proxy with credentials (WMS GetFeatureInfo). A layer drawn as
 * shapes (T6) is resolved **locally**: its features are already in the browser,
 * and its WMS layer is hidden in that mode — the server would answer nothing,
 * which is exactly the bug this closes.
 *
 * Throws ApiError so enforcement states (401/403/429/503) are handled like
 * tile errors.
 */
export async function queryFeatures(
  map: Map,
  wmsLayers: WmsTileLayer[],
  coordinate: Coordinate,
  vector: VectorModeController | null = null,
): Promise<FeatureHit[]> {
  const resolution = map.getView().getResolution()
  if (resolution === undefined) return []

  const hits: FeatureHit[] = []
  for (const layer of [...wmsLayers].reverse()) {
    const info = layerInfoOf(layer)

    if (vector?.isVector(layer)) {
      // Same cap as the WMS branch below, so one click cannot dump a hundred
      // overlapping shapes into the panel.
      for (const feature of vector.hitsAt(layer, coordinate, resolution * HIT_TOLERANCE_PX).slice(0, 10)) {
        const properties = { ...(feature.getProperties() as Record<string, unknown>) }
        delete properties.geometry // the shape itself is not an attribute
        hits.push({
          layerName: info.name,
          layerTitle: info.title,
          properties: visibleProperties(info.name, info.title, properties),
        })
      }
      continue
    }

    if (!layer.getVisible()) continue
    if (!info.queryable) continue
    const url = layer
      .getSource()
      ?.getFeatureInfoUrl(coordinate, resolution, 'EPSG:3857', {
        INFO_FORMAT: 'application/json',
        FEATURE_COUNT: 10,
      })
    if (!url) continue

    const res = await fetch(url, { credentials: 'include' })
    if (!res.ok) throw await parseErrorResponse(res)
    const collection = (await res.json()) as GeoJsonFeatureCollection
    for (const feature of collection.features ?? []) {
      hits.push({
        layerName: info.name,
        layerTitle: info.title,
        properties: visibleProperties(info.name, info.title, feature.properties ?? {}),
      })
    }
  }
  return hits
}
